import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import ReportStatCard from './ReportStatCard';
import { projectService } from '../../../services/projectService';

const BudgetAnalysisTab = ({ dateRange }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await projectService?.getAll();
      if (fetchError) throw fetchError;
      setProjects(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const projectsWithBudget = projects?.filter(p => p?.totalBudget && parseFloat(p?.totalBudget) > 0);
  const totalBudget = projectsWithBudget?.reduce((sum, p) => sum + parseFloat(p?.totalBudget || 0), 0);
  const totalActual = projectsWithBudget?.reduce((sum, p) => sum + parseFloat(p?.actualCost || p?.totalBudget || 0), 0);
  const variance = totalBudget - totalActual;
  const variancePct = totalBudget > 0 ? Math.round((variance / totalBudget) * 100) : 0;

  const budgetChartData = projectsWithBudget?.slice(0, 8)?.map(p => ({
    name: (p?.projectName || p?.name || '')?.substring(0, 15) + ((p?.projectName || p?.name || '')?.length > 15 ? '...' : ''),
    budget: parseFloat(p?.totalBudget || 0),
    actual: parseFloat(p?.actualCost || p?.totalBudget || 0),
    variance: parseFloat(p?.totalBudget || 0) - parseFloat(p?.actualCost || p?.totalBudget || 0)
  }));

  const overBudgetProjects = projectsWithBudget?.filter(p =>
    parseFloat(p?.actualCost || 0) > parseFloat(p?.totalBudget || 0)
  )?.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <Icon name="AlertCircle" size={20} className="inline mr-2" />{error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ReportStatCard
          title="Total Budget"
          value={`${(totalBudget / 1000)?.toFixed(0)}K`}
          icon="DollarSign"
          color="accent"
          subtitle={`${projectsWithBudget?.length} projects`}
          trend={0}
          trendLabel=""
        />
        <ReportStatCard
          title="Total Actual"
          value={`${(totalActual / 1000)?.toFixed(0)}K`}
          icon="Receipt"
          color="blue"
          subtitle="Costs incurred"
          trend={0}
          trendLabel=""
        />
        <ReportStatCard
          title="Budget Variance"
          value={`${variance >= 0 ? '+' : ''}${(Math.abs(variance) / 1000)?.toFixed(0)}K`}
          icon={variance >= 0 ? 'TrendingDown' : 'TrendingUp'}
          color={variance >= 0 ? 'green' : 'red'}
          subtitle={`${variancePct >= 0 ? '+' : ''}${variancePct}% vs budget`}
          trend={variancePct}
          trendLabel="vs budget"
        />
        <ReportStatCard
          title="Over Budget"
          value={overBudgetProjects}
          icon="AlertTriangle"
          color="yellow"
          subtitle="Projects exceeding budget"
          trend={0}
          trendLabel=""
        />
      </div>

      <div className="bg-card rounded-lg border border-border p-5">
        <h3 className="text-base font-semibold text-foreground mb-4">Budget vs Actual by Project</h3>
        {budgetChartData?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={budgetChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000)?.toFixed(0)}K`} />
              <Tooltip formatter={(value) => [`$${value?.toLocaleString()}`, '']} />
              <Bar dataKey="budget" name="Budget" fill="#3B82F6" radius={[4,4,0,0]} />
              <Bar dataKey="actual" name="Actual" fill="#10B981" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No budget data available</div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border">
        <div className="p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Budget Breakdown by Project</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Project</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Budget</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Actual</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Variance</th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {projectsWithBudget?.map(p => {
                const budget = parseFloat(p?.totalBudget || 0);
                const actual = parseFloat(p?.actualCost || p?.totalBudget || 0);
                const diff = budget - actual;
                const isOver = diff < 0;
                return (
                  <tr key={p?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-foreground">{p?.projectName || p?.name}</p>
                      <p className="text-xs text-muted-foreground">{p?.projectNumber}</p>
                    </td>
                    <td className="px-5 py-3 text-sm text-right text-foreground">${budget?.toLocaleString()}</td>
                    <td className="px-5 py-3 text-sm text-right text-foreground">${actual?.toLocaleString()}</td>
                    <td className={`px-5 py-3 text-sm text-right font-medium ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                      {isOver ? '-' : '+'}${Math.abs(diff)?.toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isOver ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {isOver ? 'Over Budget' : 'On Track'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {projectsWithBudget?.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">No budget data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetAnalysisTab;
